/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM, useTranslate } from "@adapptio/lib-components";
import React, { useCallback, useRef } from "react";
import { termsEditorCommon as terms } from "../../../terms/common";
import { TDialogComponent } from "../../../dialogs/Dialog";
import { DialogHeader } from "../../../ui/components/Dialogs/DialogHeader";
import { DIALOG_SIZE, DialogPanel } from "../../../ui/components/Dialogs/DialogPanel";
import { RenameDialog } from "./RenameDialog";
import { FieldText } from "../../../ui/components/Fields/FieldText";
import { Typography } from "../../../ui/components/Typography";
import { DialogContent } from "../../../ui/components/Dialogs/DialogContent";
import { useEnhancedEffect } from "../../../ui/hooks/useEnhancedEffect";
import { useFieldState } from "../../../ui/hooks/useFieldGroup";
import { SCHEMA_VALIDATION_ERROR_TYPE, ValidatorString } from "@adapptio/lib-blueprint";
import { DialogButtons } from "../../../ui/components/Dialogs/DialogButtons";
import { ACTION_BUTTON_SIZE, ACTION_BUTTON_STYLE, ActionButton } from "../../../ui/components/ActionButton";
import { Form } from "../../../ui/components/Form";

export const RenameDialogComponent: TDialogComponent<RenameDialog> = ({ dialog }) => {
	const baseClass = BEM("edt-module-core-rename-dialog");
	const inputRef = useRef<HTMLInputElement>();
	const t = useTranslate();
	const params = dialog.getParams();
	const oldName = params.resourceName;

	const translations = {
		title: t("editor", terms.dialog.resourceRename.title),
		description: t("editor", terms.dialog.resourceRename.description, { resourceName: oldName }),
		actions: {
			confirm: t("editor", terms.dialog.resourceRename.actions.confirm),
			cancel: t("editor", terms.dialog.resourceRename.actions.cancel)
		},
		messages: {
			name: {
				duplicateIdentifier: t(
					"editor",
					terms.dialog.resourceRename.messages.name.duplicateIdentifier
				)
			}
		}
	};

	const [ fieldState, setFieldState ] = useFieldState(
		{
			name: {
				defaultValue: oldName,
				validators: [
					ValidatorString({ required: true, min: 1 }),
					(value: string) => {
						if (dialog.checkIfResourceExists("", value)) {
							return [
								{
									type: SCHEMA_VALIDATION_ERROR_TYPE.DUPLICATE_IDENTIFIER,
									message: translations.messages.name.duplicateIdentifier,
									metaData: {}
								}
							];
						} else {
							return [];
						}
					}
				]
			}
		},
		[ dialog ]
	);

	const handleClose = useCallback(() => {
		dialog.close({
			newName: null
		});
	}, [ dialog ]);

	const handleRename = useCallback(() => {
		const newName = fieldState.name.value;

		dialog.close({
			newName
		});
	}, [ dialog, fieldState ]);

	useEnhancedEffect(() => {
		if (inputRef.current && document.activeElement === inputRef.current) {
			inputRef.current.setSelectionRange(0, inputRef.current.value?.length);
		}
	}, []);

	return (
		<DialogPanel size={DIALOG_SIZE.SMALL} className={baseClass.toClassName()}>
			<Form onSubmit={handleRename} noValidate>
				<DialogHeader title={translations.title} icon="mdi/pencil" onClose={handleClose} />
				<DialogContent>
					<label className={baseClass.element("field-row").toClassName()}>
						<Typography className={baseClass.element("field-label").toClassName()}>
							{translations.description}
						</Typography>
						<FieldText
							className={baseClass.element("field-input").toClassName()}
							value={fieldState.name.value}
							validationErrors={fieldState.name.validationErrors}
							onChange={setFieldState.name}
							autoFocus
							grow
							inputRef={inputRef}
						/>
					</label>
				</DialogContent>
				<DialogButtons>
					<ActionButton
						label={translations.actions.cancel}
						icon="mdi/close"
						type="button"
						style={ACTION_BUTTON_STYLE.DEFAULT}
						size={ACTION_BUTTON_SIZE.LARGE}
						onClick={handleClose}
					/>
					<ActionButton
						label={translations.actions.confirm}
						icon="mdi/check"
						type="submit"
						style={ACTION_BUTTON_STYLE.PRIMARY}
						size={ACTION_BUTTON_SIZE.LARGE}
						onClick={handleRename}
						disabled={fieldState.name.validationErrors.length > 0 || fieldState.name.value === ""}
					/>
				</DialogButtons>
			</Form>
		</DialogPanel>
	);
};
